import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { defaultMemoize } from 'reselect';

import { noop } from 'ui-components/default-props/noop';

import { useMultiPackageShipments } from '../../../../feature-flags/hooks/use-multi-package-shipments';
import PackageDropdown from '../../../../fulfillment-planner/package/dropdown/';
import {
  groupByCarrierAndCustom,
  groupByCustomAndAddGenericPackage,
} from '../../../../fulfillment-planner/package/dropdown/package-group-resolver';
import { useFilteredPackages } from '../../../../hooks/use-filtered-packages';
import { STANDARD_PACKAGE_ID } from '../../../../services/package-service';
import Global from '../../../../store/global-selectors';
import { setupPackageSet } from '../../../modals/multi-package-modal/setup-package-set';

const getCarriers = (state) => Global.selectReferenceDatagetSellerCarriers(state) || [];

const getPackageGroups = defaultMemoize((onlyCustomOrGenericPackages, packages, state) => {
  return (
    onlyCustomOrGenericPackages ? groupByCustomAndAddGenericPackage(packages) : groupByCarrierAndCustom(packages, getCarriers(state))
  ).filter((group) => group.packages.length);
});

/**
 * @typedef {import('../../../../prop-types/package').PackagePropType} PackagePropType
 * @typedef {import('../../../../components/modals/multi-package-modal/package-set').SetupPackageSet} SetupPackageSet
 */
/**
 * A dropdown component for selecting packages in bulk actions.
 * Allows selection of individual packages or package sets based on feature flags.
 * @param {object} props - The component props
 * @param {((pkg: PackagePropType, state: any) => boolean)[]} [props.packageFilters] - Array of filter functions to apply to packages
 * @param {(selectedPackage: PackagePropType | SetupPackageSet) => void} [props.onPackageSelected] - Callback function called when a package is selected (defaults to noop)
 * @param {string[]} [props.selectedPackageIds] - Array of currently selected package IDs
 * @returns {JSX.Element} The rendered PackageDropdown component
 */
const BulkActionPackageDropdown = ({ packageFilters, onPackageSelected = noop, selectedPackageIds }) => {
  const enableMultiPackageShipments = useMultiPackageShipments();
  const filteredPackages = useFilteredPackages(packageFilters);
  const packageSets = useSelector(Global.selectSellerPackageSets);
  const sellerDefaultDimensionsUnit = useSelector(Global.selectSellerDefaultDimensionUnit);

  // TODO: return combined packages when enableMultiPackageShipments flag is removed
  const packages = useMemo(() => {
    return enableMultiPackageShipments ? [...filteredPackages, ...packageSets] : filteredPackages;
  }, [enableMultiPackageShipments, filteredPackages, packageSets]);

  const onlyCustomOrGenericPackages = packages.every(({ isCustom, packageTypeId }) => isCustom || packageTypeId === STANDARD_PACKAGE_ID);
  const packageGroups = useSelector((state) => getPackageGroups(onlyCustomOrGenericPackages, packages, state));

  const onChange = useCallback(
    (packageId) => {
      const selectedPackage = packages.filter((p) => p.packageId === packageId)[0];
      onPackageSelected(selectedPackage);
    },
    [onPackageSelected, packages],
  );

  const onChangePackageSet = useCallback(
    (packageSetId) => {
      const packageSet = packageSets.find((p) => p.packageSetId === packageSetId);
      const packageSetPackages = setupPackageSet(packageSet, sellerDefaultDimensionsUnit);
      const packageSetWithParsedPackages = {
        ...packageSet,
        packages: packageSetPackages,
      };

      onPackageSelected(packageSetWithParsedPackages);
    },
    [onPackageSelected, packageSets, sellerDefaultDimensionsUnit],
  );

  return (
    <PackageDropdown
      onChange={onChange}
      onChangePackageSet={onChangePackageSet}
      packageGroups={packageGroups}
      selectedPackageIds={selectedPackageIds}
    />
  );
};

export default BulkActionPackageDropdown;
